import React, { FC, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { Stack } from "@mui/material";

import { CatalogLocalStorage } from "@pages/Catalog";
import CheckBoxInput from "@pages/Catalog/components/Filters/CheckBoxInput";
import SliderInput from "@pages/Catalog/components/Filters/SliderInput";
import { getDirtyAmount } from "@pages/Catalog/utils";

import { useIsDesktop } from "@hooks/layout";

import { useAppDispatch, useAppDispatchUnwrap } from "@store/hooks";
import { setAvailableTagsState } from "@store/reducers/filters";

import { ICatalogueFilters, IFilters, ITags } from "@models/models";
import { scrollToAnchorByRef } from "@utils/scrollToAnchor";
import { useFormik } from "formik";
import uniqueId from "lodash/uniqueId";

import { availableTags as availableTagsAction } from "@/store/actions/filters";
import { Button, FilterSelection } from "@/ui-kit";

import { CatalogTab } from "../utils";

// Display name overrides for filter categories from API
// Keys are the original API names, values are what gets shown in the UI
const filterDisplayNames: Record<string, string> = {
  "Available For": "License",
};

const getFilterDisplayName = (name: string): string => filterDisplayNames[name] || name;

// Controls the display order of tag-based filter sections.
// Lower numbers appear first. Unlisted categories default to 50.
const filterOrder: Record<string, number> = {
  "Genre": 1,
  "Mood": 2,
  "Spirituality": 3,
  "Main Instrument": 4,
  "Available For": 5,
  "Key": 100,
};

interface IFilterForm {
  maxLength: number;
  maxBpm: number;
  handleSubmitForm: (values: any) => void;
  initialFiltersValues: IFilters;
  uniqueTags: [string, ITags[]][];
  availableTags: Record<string, boolean>;
  dataFilters: ICatalogueFilters;
  handleClose?: () => void;
  setButton?: React.Dispatch<React.SetStateAction<JSX.Element>>;
}

const Filters: FC<IFilterForm> = ({
  dataFilters,
  uniqueTags,
  availableTags,
  handleSubmitForm,
  initialFiltersValues,
  maxBpm,
  maxLength,
  handleClose,
  setButton,
}) => {
  const { t } = useTranslation("catalogue", { keyPrefix: "filters" });
  const isDesktop = useIsDesktop();
  const lastFilterRef = useRef<HTMLElement>(null);
  const dispatch = useAppDispatchUnwrap();
  const syncDispatch = useAppDispatch();

  const isAlbum = localStorage.getItem(CatalogLocalStorage.CATALOG_TAB);

  const convertLengthAndBpm = (filterValues: IFilters) => {
    const filters: Partial<{ bpm: number[]; length: number[] }> = {};
    const length = filterValues.length[0] !== 0 || filterValues.length[1] !== maxLength ? filterValues.length : [0, 0];
    const bpm = filterValues.bpm[0] !== 0 || filterValues.bpm[1] !== maxBpm ? filterValues.bpm : [0, 0];

    filters.bpm = !bpm.every((el) => el === 0) ? bpm : null;
    filters.length = !length.every((el) => el === 0) ? length : null;
    return filters;
  };

  const { handleSubmit, values, errors, setFieldValue, touched } = useFormik({
    initialValues: initialFiltersValues,
    onSubmit: (filterValues) => {
      const length =
        filterValues.length[0] !== 0 || filterValues.length[1] !== maxLength ? filterValues.length : [0, 0];
      const bpm = filterValues.bpm[0] !== 0 || filterValues.bpm[1] !== maxBpm ? filterValues.bpm : [0, 0];
      handleSubmitForm({ ...filterValues, length, bpm });
      handleClose?.();
    },
    enableReinitialize: true,
  });

  const activeFilterCount = (numberOfFilters: number) => {
    if (!numberOfFilters) return "";

    return ` ${numberOfFilters}`;
  };

  const onExpand = (isLast: boolean) => {
    if (isLast && !isDesktop) {
      setTimeout(() => scrollToAnchorByRef(lastFilterRef.current, undefined, undefined, { block: "end" }), 200);
    }
  };

  useEffect(() => {
    if (setButton) {
      setButton(
        !isDesktop && (
          <Button isFullWidth onClick={handleSubmit} sx={{ marginTop: "16px" }}>
            {t("applyFilters", { count: getDirtyAmount(values) })}
          </Button>
        ),
      );
    }
  }, [values]);

  useEffect(() => {
    const filtersAndSort: IFilters = { filter: { ...values, ...convertLengthAndBpm(values) } };

    if (filtersAndSort.filter.tags.length || filtersAndSort.filter.composer) {
      dispatch(availableTagsAction({ params: { ...filtersAndSort }, isTrack: isAlbum !== CatalogTab.ALBUM }));
    } else {
      syncDispatch(setAvailableTagsState(null));
    }
  }, [values]);

  return (
    <form style={{ width: "100%" }}>
      <Stack spacing="0px">
        <FilterSelection
          title={`${t("composer")}${activeFilterCount(values.composer.length)}`}
          defaultExpanded={!!values.composer.length}
        >
          {dataFilters?.composers?.map((el) => (
            <CheckBoxInput
              key={uniqueId("filter-composer_")}
              fieldName="composer"
              label={el}
              values={values}
              errors={errors}
              setFieldValue={setFieldValue}
              touched={touched}
              handleSubmit={isDesktop ? handleSubmit : null}
            />
          ))}
        </FilterSelection>
        {[...uniqueTags].filter(([key]) => key !== "Available For").sort((a, b) => (filterOrder[a[0]] ?? 50) - (filterOrder[b[0]] ?? 50)).map(([key, value]) => (
          <FilterSelection
            key={`${key}_${value}`}
              title={`${getFilterDisplayName(key)}${activeFilterCount(value.filter((el) => values.tags.includes(el._id)).length)}`}
              defaultExpanded={value.some((el) => values.tags.includes(el._id))}
              tags={value}
            >
              {value
                .sort((a, b) => a.value.localeCompare(b.value))
                .map((element) => (
                  <CheckBoxInput
                    key={element._id}
                    fieldName="tags"
                    label={element.value}
                    id={element._id}
                    disabled={availableTags && !availableTags[element._id]}
                    values={values}
                    errors={errors}
                    setFieldValue={setFieldValue}
                    touched={touched}
                    handleSubmit={isDesktop ? handleSubmit : null}
                  />
                ))}
            </FilterSelection>
        ))}

        <FilterSelection title={t("length")} defaultExpanded={!values.length.every((el) => el === 0)}>
          <SliderInput
            fieldName="length"
            values={values}
            maxSliderLength={maxLength}
            setFieldValue={setFieldValue}
            handleSubmit={isDesktop ? handleSubmit : null}
          />
        </FilterSelection>

        <div ref={lastFilterRef as React.RefObject<HTMLDivElement>}>
          <FilterSelection
            title={t("bpm")}
            defaultExpanded={!values.bpm.every((el) => el === 0)}
            onExpand={() => onExpand(true)}
          >
            <SliderInput
              fieldName="bpm"
              values={values}
              maxSliderLength={maxBpm}
              setFieldValue={setFieldValue}
              handleSubmit={isDesktop ? handleSubmit : null}
            />
          </FilterSelection>
        </div>
      </Stack>
    </form>
  );
};
export default Filters;
